import {
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Text,
  useBreakpointValue,
  VStack
} from '@chakra-ui/react'
import React, { useState } from 'react'
import type { CancelAppointmentDto } from '../../core/api/__generated__/clinician-api/models/cancelAppointmentDto'
import { CancelAppointmentDtoCancellationMode } from '../../core/api/__generated__/clinician-api/models/cancelAppointmentDtoCancellationMode'

interface AppointmentCancellationModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (data: CancelAppointmentDto) => void
  patientName?: string
  isLoading?: boolean
}

export const AppointmentCancellationModal: React.FC<
  AppointmentCancellationModalProps
> = ({
  isOpen,
  onClose,
  onConfirm,
  patientName = 'your client',
  isLoading = false
}) => {
  const [cancellationMode, setCancellationMode] =
    useState<CancelAppointmentDtoCancellationMode>(
      CancelAppointmentDtoCancellationMode.single
    )

  const modalSize = useBreakpointValue({
    base: 'full',
    sm: 'md'
  })

  const handleCancel = () => {
    // Reset to default when closing
    setCancellationMode(CancelAppointmentDtoCancellationMode.single)
    onClose()
  }

  const handleContinue = () => {
    // Instead of hardcoding sendNotification, let's pass the cancellation mode
    // and let the parent handle the notification flow
    const data: CancelAppointmentDto = {
      sendNotification: false, // This will be determined by the notification modal
      cancellationMode
    }
    onConfirm(data)
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      isCentered
      size={modalSize}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent mx={4} borderRadius="16px">
        <ModalHeader
          textAlign="center"
          fontSize="18px"
          fontWeight="bold"
          pt={4}
          pb={3}
          px={4}
        >
          Recurring event
        </ModalHeader>
        <ModalCloseButton size="md" _focus={{ outline: 'none' }} />

        <ModalBody px={6} pb={6}>
          <VStack spacing={4} align="stretch">
            <Text fontSize="16px" color="gray.800" lineHeight="22px">
              You've got recurring sessions with {patientName}. Want to delete
              just this one or all going forward?
            </Text>

            <RadioGroup
              value={cancellationMode}
              onChange={value =>
                setCancellationMode(
                  value as CancelAppointmentDtoCancellationMode
                )
              }
            >
              <VStack spacing={1} align="stretch">
                <Radio
                  value={CancelAppointmentDtoCancellationMode.single}
                  size="md"
                  colorScheme="blue"
                  p={3}
                  borderRadius="8px"
                  border="1px solid"
                  borderColor="gray.100"
                  _checked={{
                    borderColor: 'blue.500',
                    bg: 'blue.50'
                  }}
                  _focus={{ outline: 'none' }}
                >
                  <Text fontSize="16px" ml={2}>
                    Delete this session only
                  </Text>
                </Radio>

                <Radio
                  value={CancelAppointmentDtoCancellationMode.remaining}
                  size="md"
                  colorScheme="blue"
                  p={3}
                  borderRadius="8px"
                  border="1px solid"
                  borderColor="gray.100"
                  _checked={{
                    borderColor: 'blue.500',
                    bg: 'blue.50'
                  }}
                  _focus={{ outline: 'none' }}
                >
                  <Text fontSize="16px" ml={2}>
                    Delete this and all future sessions
                  </Text>
                </Radio>
              </VStack>
            </RadioGroup>

            <HStack spacing={2.5} pt={4}>
              <Button
                flex={1}
                variant="outline"
                colorScheme="gray"
                onClick={handleCancel}
                size="lg"
                borderRadius="8px"
                _focus={{ outline: 'none' }}
                disabled={isLoading}
              >
                Cancel
              </Button>

              <Button
                flex={1}
                colorScheme="blue"
                onClick={handleContinue}
                size="lg"
                borderRadius="8px"
                _focus={{ outline: 'none' }}
                isLoading={isLoading}
                loadingText="Continuing..."
              >
                Continue
              </Button>
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default AppointmentCancellationModal
